import React, { useEffect, useState } from "react";
import {
  Tab,
  Tabs,
  CircularProgress,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  FormControl,
  TextField,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  LinearProgress,
  InputLabel,
  NativeSelect,
  FormHelperText,
} from "@material-ui/core";
import { TabPanel } from "../components/TabPanel";
import { AlteracaoResult, Instalacao, StatusPista } from "../models/posto";
import { getCombustivelDefByNome } from "../models/combustivel";
import { SmallestTableCell } from "../components/TableCell";
import { AvatarCombustivel } from "../components/avatarCombustivel";
import numeral from "numeral";
import { NumberFormatCustom } from "../components/NumberInput";
import { LocalGasStation } from "@material-ui/icons";
import { useApi, useFetcher } from "../components/api";
import { MySnackToast, useToast } from "../components/MySnackToast";

interface PrecoPorBico extends Record<string, any> {
  bico: number;
  sigla: string;
  combustivelCod: number;
  combustivelNome: string;
  preco: number;
  novoPreco?: number;
  alterando?: boolean;
}

interface PrecoPorComb extends Record<string, any> {
  sigla: string;
  combustivelCod: number;
  combustivelNome: string;
  preco: number;
  novoPreco?: number;
  alterando?: boolean;
}

const REFRESH_INTERVAL_MS = 5000;

export const PrecosPage = () => {
  const [instalacoes, setInstalacoes] = useState<Instalacao[]>([]);
  const [instalacaoSelecionada, setInstalacaoSelecionada] =
    useState<Instalacao>();
  const [tabIndex, setTabIndex] = React.useState(0);
  const [precosPorComb, setPrecosPorComb] = useState<{
    [sigla: string]: PrecoPorComb;
  }>({});
  const [precosPorBico, setPrecosPorBico] = useState<{
    [bico: number]: PrecoPorBico;
  }>({});
  const [showDialogAlterarPreco, setShowDialogAlterarPreco] = useState(false);
  const [alterandoPrecoMessage, setAlterandoPrecoMessage] = useState<
    string | undefined
  >(undefined);
  const { api } = useApi();
  const toast = useToast();

  // O Cloud le o srvaba pela tunnel a cada chamada, entao a tela busca sozinha
  // em vez de esperar um push.
  const { response, error, mutate } = useFetcher<StatusPista>(
    "statusPistas",
    REFRESH_INTERVAL_MS,
  );

  const status = response?.data?.data ?? {};
  const carregando = response === undefined && error === undefined;

  // on status change
  useEffect(() => {
    const installs = Object.keys(status).map(
      (x) =>
        ({
          installCode: status[x].ic,
          empresa: {
            codigo: status[x].cod,
            nome: status[x].nome,
          },
        }) as Instalacao,
    );
    setInstalacoes(installs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response]);

  useEffect(() => {
    if (instalacoes?.length > 0 && instalacaoSelecionada === undefined)
      setInstalacaoSelecionada(instalacoes[0]);
  }, [instalacoes, instalacaoSelecionada]);

  function groupByComb(precos: PrecoPorComb[], key: string) {
    return precos.reduce((prev: PrecoPorComb[], current: PrecoPorComb) => {
      let el = prev.find((rec: any) => rec && rec[key] === current[key]);
      if (!el) {
        prev.push(current);
      } else {
        if (current["preco"] < el["preco"]) el["preco"] = current["preco"];
      }
      return prev;
    }, []);
  }

  const handleTabsChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setTabIndex(newValue);
  };

  const handlePrecoPorCombChange = (
    event: any,
    porCombustivel: PrecoPorComb,
  ) => {
    const newValue = { ...porCombustivel, novoPreco: +event.target.value };
    changePrecosPorComb(newValue);
  };

  const handlePrecoPorBicoChange = (event: any, porBico: PrecoPorBico) => {
    const newValue = { ...porBico, novoPreco: +event.target.value };
    changePrecosPorBico(newValue);
  };

  const changePrecosPorBico = (precoPorBico: PrecoPorBico) => {
    setPrecosPorBico((value) => ({
      ...value,
      [precoPorBico.bico]: precoPorBico,
    }));
  };

  const changePrecosPorComb = (precoPorComb: PrecoPorComb) => {
    setPrecosPorComb((value) => ({
      ...value,
      [precoPorComb.sigla]: precoPorComb,
    }));
  };

  const handleEmpresaSelectChange = (event: any) => {
    const newValue = instalacoes.find(
      (x) => x.installCode === event.target.value,
    );
    setInstalacaoSelecionada(newValue);
  };

  const handleButtonAlterarPrecoClick = () => setShowDialogAlterarPreco(true);
  const handleCancelarAlteracaoPreco = () => setShowDialogAlterarPreco(false);

  const handleConfirmarAlteracaoPreco = async () => {
    setShowDialogAlterarPreco(false);
    setAlterandoPrecoMessage("Alterando preços");

    if (tabIndex === 0) {
      for (let key in precosPorComb) {
        var precoPorComb = precosPorComb[key];

        if (precoPorComb.novoPreco === undefined) continue;

        const model = {
          empresaCod: instalacaoSelecionada?.empresa.codigo,
          targetId: instalacaoSelecionada?.installCode,
          combustivelCod: precoPorComb.combustivelCod.toString(),
          novoPreco: precoPorComb.novoPreco,
        };

        try {
          precoPorComb.alterando = true;
          changePrecosPorComb(precoPorComb);

          const resp = await api.post<AlteracaoResult>(
            "alterarPrecoCombustivel",
            model,
            10000,
          );
          if (resp.data.result === true) {
            toast.success(
              `Preço do combustível ${precoPorComb.sigla} alterado de ${precoPorComb.preco} para ${precoPorComb.novoPreco} com sucesso!`,
            );
          } else {
            toast.fail(
              `Falha ao alterar preço do combustível ${precoPorComb.sigla}! Erro: ${resp.data.errorMessage}`,
            );
          }
        } catch (error) {
          toast.fail(`Falha ao alterar preço! Erro: ${error.message}`);
        }

        precoPorComb.novoPreco = undefined;
        precoPorComb.alterando = false;
        changePrecosPorComb(precoPorComb);
      }
    } else {
      for (let key in precosPorBico) {
        var precoPorBico = precosPorBico[+key];

        if (precoPorBico.novoPreco === undefined) continue;

        const model = {
          empresaCod: instalacaoSelecionada?.empresa.codigo,
          targetId: instalacaoSelecionada?.installCode,
          BicoCod: precoPorBico.bico,
          combustivelCod: precoPorBico.combustivelCod.toString(),
          novoPreco: precoPorBico.novoPreco,
        };

        try {
          precoPorBico.alterando = true;
          changePrecosPorBico(precoPorBico);

          const resp = await api.post<AlteracaoResult>(
            "alterarPrecoBico",
            model,
            10000,
          );

          if (resp.data.result === true) {
            toast.success(
              `Preço do bico ${precoPorBico.bico} alterado de ${precoPorBico.preco} para ${precoPorBico.novoPreco} com sucesso!`,
            );
          } else {
            toast.fail(
              `Falha ao alterar preço do bico ${precoPorBico.bico}! Erro: ${resp.data.errorMessage}`,
            );
          }
        } catch (error) {
          toast.fail(`Falha ao alterar preço! Erro: ${error.message}`);
        }

        precoPorBico.novoPreco = undefined;
        precoPorBico.alterando = false;
        changePrecosPorBico(precoPorBico);
      }
    }
    setAlterandoPrecoMessage(undefined);

    // O preco valido e o que a bomba assumiu; relemos em vez de acreditar no estado local.
    mutate();
  };

  const statusAutomacao =
    status && instalacaoSelecionada
      ? status[instalacaoSelecionada.installCode]
      : undefined;
  const statusBicos = statusAutomacao?.status ?? [];
  const porBico: PrecoPorBico[] = statusBicos.map((x) => {
    return {
      bico: x.id,
      sigla: getCombustivelDefByNome(x.combustivelNome).sigla,
      combustivelCod: x.combustivelCod,
      combustivelNome: x.combustivelNome,
      preco: x.preco,
    };
  });
  const combustiveis: PrecoPorComb[] = statusBicos.map((x) => {
    return {
      sigla: getCombustivelDefByNome(x.combustivelNome).sigla,
      combustivelCod: x.combustivelCod,
      combustivelNome: x.combustivelNome,
      preco: x.preco,
    };
  });
  const porCombustivel = groupByComb(combustiveis, "sigla");

  return (
    <div>
      {carregando ? (
        <CircularProgress />
      ) : (
        <div>
          <div style={{ fontSize: "10px", width: "100%", textAlign: "center" }}>
            <p>Atualizado em: {statusAutomacao?.atualizadoEm}</p>
          </div>

          {instalacoes.length > 1 ? (
            <FormControl style={{ width: "100%" }}>
              <InputLabel htmlFor="select-instalacoes">Empresas</InputLabel>
              <NativeSelect
                fullWidth={true}
                value={instalacaoSelecionada}
                onChange={handleEmpresaSelectChange}
                inputProps={{
                  id: "select-instalacoes",
                }}
              >
                {instalacoes.map((item, idx) => (
                  <option key={idx} value={item.installCode}>
                    {item.installCode}: {item.empresa.codigo} -{" "}
                    {item.empresa.nome}
                  </option>
                ))}
              </NativeSelect>
              <FormHelperText>Escolha a empresa</FormHelperText>
            </FormControl>
          ) : (
            <></>
          )}

          {statusAutomacao?.error && (
            <div style={{ textAlign: "center", padding: "16px 0" }}>
              <p>Posto indisponível no momento.</p>
              <p style={{ fontSize: "12px" }}>{statusAutomacao.error}</p>
            </div>
          )}

          <Tabs
            value={tabIndex}
            indicatorColor="primary"
            textColor="primary"
            onChange={handleTabsChange}
            variant="fullWidth"
          >
            <Tab label="Por Combustíveis" />
            <Tab label="Por Bicos" />
          </Tabs>
          <TabPanel value={tabIndex} index={0}>
            <TableContainer>
              <Table size="small" aria-label="custom pagination table">
                <TableBody>
                  <TableRow key={"0"}>
                    <SmallestTableCell variant="head">
                      Combustível
                    </SmallestTableCell>
                    <SmallestTableCell variant="head" align="right">
                      Preço
                    </SmallestTableCell>
                    <SmallestTableCell variant="head" align="right">
                      Novo Preço
                    </SmallestTableCell>
                  </TableRow>

                  {porCombustivel?.map((x: PrecoPorComb) => {
                    const novoPreco =
                      (precosPorComb[x.sigla]?.novoPreco ?? 0) > 0
                        ? precosPorComb[x.sigla]?.novoPreco
                        : "";
                    const preco = x.preco;

                    return (
                      <TableRow key={x.sigla}>
                        <SmallestTableCell>
                          <AvatarCombustivel combustivelSigla={x.sigla} />
                        </SmallestTableCell>
                        <SmallestTableCell align="right">
                          {precosPorComb[x.sigla]?.alterando === true ? (
                            <CircularProgress />
                          ) : (
                            <>R$ {numeral(preco).format(",0.000")}</>
                          )}
                        </SmallestTableCell>
                        <SmallestTableCell align="right">
                          <FormControl>
                            <TextField
                              value={novoPreco}
                              onChange={(ev) => handlePrecoPorCombChange(ev, x)}
                              InputProps={{
                                inputComponent: NumberFormatCustom as any,
                                name: "novoPreco",
                              }}
                            />
                          </FormControl>
                        </SmallestTableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>
          <TabPanel value={tabIndex} index={1}>
            <Grid container spacing={2}>
              {porBico?.map((x: PrecoPorBico) => {
                const novoPreco =
                  (precosPorBico[x.bico]?.novoPreco ?? 0) > 0
                    ? precosPorBico[x.bico]?.novoPreco
                    : "";
                const preco = x.preco;
                return (
                  <Grid key={x.bico} item xl={2} lg={2} md={3} sm={4} xs={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Grid
                          container
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Typography
                            variant="h6"
                            style={{ display: "flex", alignItems: "center" }}
                          >
                            <LocalGasStation style={{ paddingRight: "4px" }} />
                            {x.bico}
                          </Typography>
                          <AvatarCombustivel
                            combustivelSigla={x.sigla}
                            size="small"
                          />
                        </Grid>

                        {precosPorBico[x.bico]?.alterando === true ? (
                          <Grid
                            container
                            direction="row"
                            justifyContent="center"
                            alignItems="center"
                          >
                            <CircularProgress style={{ padding: "17px 0px" }} />
                          </Grid>
                        ) : (
                          <>
                            <Typography
                              variant="overline"
                              display="block"
                              align="center"
                            >
                              R$ {numeral(preco).format(",0.000")}
                            </Typography>
                            <FormControl
                              size="small"
                              variant="outlined"
                              fullWidth
                            >
                              <TextField
                                style={{ height: "20px" }}
                                size="small"
                                label="Novo Preço"
                                variant="outlined"
                                value={novoPreco}
                                onChange={(ev) =>
                                  handlePrecoPorBicoChange(ev, x)
                                }
                                InputProps={{
                                  inputComponent: NumberFormatCustom as any,
                                  name: "novoPreco",
                                }}
                              />
                            </FormControl>
                          </>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </TabPanel>

          <Button
            fullWidth
            variant="contained"
            color="primary"
            style={{ marginBottom: "24px" }}
            onClick={handleButtonAlterarPrecoClick}
            disabled={
              statusAutomacao?.permiteAtualizar === false ||
              alterandoPrecoMessage !== undefined
            }
          >
            Alterar
          </Button>

          <MySnackToast ti={toast} />

          {alterandoPrecoMessage && <LinearProgress />}

          <Dialog
            open={showDialogAlterarPreco}
            onClose={handleCancelarAlteracaoPreco}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogTitle id="alert-dialog-title">
              Confirma alteração de preço?
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                O preço será alterado diretamente na automação/bombas. Confirma?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCancelarAlteracaoPreco} color="primary">
                Cancelar
              </Button>
              <Button
                onClick={handleConfirmarAlteracaoPreco}
                color="primary"
                autoFocus
              >
                Ok, alterar
              </Button>
            </DialogActions>
          </Dialog>
        </div>
      )}
    </div>
  );
};
